import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'github-markdown-css/github-markdown-light.css';
import Navigation from './Components/Navigation';

const CodeComponent = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  
  if (!inline && match) {
    return (
      <SyntaxHighlighter
        style={oneLight}
        language={match[1]}
        PreTag="div"
        customStyle={{
          background: 'transparent',
          padding: '16px',
          margin: 0
        }}
        codeTagProps={{
          style: {
            background: 'transparent'
          }
        }}
        {...props}
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    );
  }
  
  return <code className={className} {...props}>{children}</code>;
};

const NAV_ITEMS = [
  { file: 'index.md', label: 'API Overview' },
  { file: 'newtalent-api.md', label: 'Create New Talent' },
  { file: 'newjob-api.md', label: 'Create New Job' },
  { file: 'newcandidate-api.md', label: 'Add Candidate to Job' }
];

const Documentation = () => {
  const [content, setContent] = useState('Loading documentation...');
  const [activeFile, setActiveFile] = useState('index.md');
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fileToLoad = params.get('file') || 'index.md';
    setActiveFile(fileToLoad);
    loadMarkdown(fileToLoad);
  }, [location.search]);

  const loadMarkdown = async (file) => {
    setContent('Loading documentation...');
    setError(null);
    
    try {
      const response = await fetch(`/docs/${file}`);
      if (!response.ok) throw new Error(`Failed to load ${file}`);
      setContent(await response.text());
    } catch (err) {
      setError(`Error: ${err.message}`);
      setContent('');
    }
  };

  const handleNavClick = (file) => {
    setActiveFile(file);
    const url = new URL(window.location);
    url.searchParams.set('file', file);
    window.history.pushState({}, '', url);
    loadMarkdown(file);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[100px]">
      <Navigation title="Documentation" showBackButton={false} />
      
      <div style={{ display: 'flex', gap: '20px', maxWidth: '1400px', margin: '0 auto', minHeight: '100vh' }}>
      <nav style={{ 
        width: '280px', 
        minWidth: '280px',
        padding: '20px', 
        backgroundColor: '#f6f8fa', 
        borderRadius: '6px',
        height: 'fit-content',
        position: 'sticky',
        top: '20px'
      }}>
        <h3>Documentation</h3>
        {NAV_ITEMS.map(item => (
          <a
            key={item.file}
            href="#"
            onClick={(e) => { e.preventDefault(); handleNavClick(item.file); }}
            style={{
              display: 'block',
              padding: '12px 16px',
              margin: '4px 0',
              color: activeFile === item.file ? '#000' : '#0366d6',
              textDecoration: 'none',
              fontWeight: activeFile === item.file ? 'bold' : 'normal',
              backgroundColor: activeFile === item.file ? '#e1e4e8' : 'transparent',
              borderRadius: '6px'
            }}
          >
            {item.label}
          </a>
        ))}
      </nav>
      
      <main style={{ 
        flex: 1, 
        minWidth: 0,
        padding: '30px', 
        backgroundColor: '#fff', 
        borderRadius: '6px', 
        border: '1px solid #e1e4e8'
      }}>
        {error ? (
          <div style={{ color: 'red', padding: '16px', backgroundColor: '#ffeef0', borderRadius: '6px' }}>
            {error}
          </div>
        ) : (
          <div className="markdown-body" style={{ 
            listStyleType: 'inherit',
          }}>
            {content === 'Loading documentation...' ? (
              <p>{content}</p>
            ) : (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{ 
                  code: CodeComponent,
                  ul: ({ children, ...props }) => (
                    <ul style={{ listStyleType: 'disc', paddingLeft: '2em' }} {...props}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children, ...props }) => (
                    <ol style={{ listStyleType: 'decimal', paddingLeft: '2em' }} {...props}>
                      {children}
                    </ol>
                  )
                }}
              >
                {content}
              </ReactMarkdown>
            )}
          </div>
        )}
      </main>
      </div>
    </div>
  );
};

export default Documentation;
